import { FC, useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import type {
  DateRange,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'

interface ValidationConfig {
  isValidRange: (start: string | null, end: string | null) => boolean
  errorMessage: string
}

export interface DateRangeSelectorProps {
  field: string
  label: string
  value: DateRange
  onChange: (params: UpdateDateRangeParams) => void
  validation?: ValidationConfig
}

export const DateRangeSelector: FC<DateRangeSelectorProps> = ({
  field,
  label,
  value,
  onChange,
  validation,
}) => {
  const handleStartDateChange = (newValue: dayjs.Dayjs | null) => {
    const newStart = newValue ? newValue.format('YYYY-MM-DD') : null
    const currentEnd = value.end

    // Validate the new range if validation is provided
    if (validation && newStart && currentEnd) {
      if (!validation.isValidRange(newStart, currentEnd)) {
        // If invalid range, don't update
        return
      }
    }

    onChange({
      field,
      range: {
        start: newStart,
        end: currentEnd,
      },
    })
  }

  const handleEndDateChange = (newValue: dayjs.Dayjs | null) => {
    const currentStart = value.start
    const newEnd = newValue ? newValue.format('YYYY-MM-DD') : null

    // Validate the new range if validation is provided
    if (validation && currentStart && newEnd) {
      if (!validation.isValidRange(currentStart, newEnd)) {
        // If invalid range, don't update
        return
      }
    }

    onChange({
      field,
      range: {
        start: currentStart,
        end: newEnd,
      },
    })
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, }}>
      <DatePicker
        label="Start Date"
        value={value.start ? dayjs(value.start) : null}
        onChange={handleStartDateChange}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            error:
              validation?.isValidRange && value.start && value.end
                ? !validation.isValidRange(value.start, value.end)
                : false,
            helperText:
              validation?.isValidRange &&
              value.start &&
              value.end &&
              !validation.isValidRange(value.start, value.end)
                ? validation.errorMessage
                : '',
          },
        }}
      />
      <DatePicker
        label="End Date"
        value={value.end ? dayjs(value.end) : null}
        onChange={handleEndDateChange}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            error:
              validation?.isValidRange && value.start && value.end
                ? !validation.isValidRange(value.start, value.end)
                : false,
            helperText:
              validation?.isValidRange &&
              value.start &&
              value.end &&
              !validation.isValidRange(value.start, value.end)
                ? validation.errorMessage
                : '',
          },
        }}
      />
    </Box>
  )
}
